import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// ---------- helpers ----------
function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id_" + Math.random().toString(16).slice(2) + "_" + Date.now();
}

function toDateInputValue(tsOrNull) {
  if (!tsOrNull) return "";
  const d = tsOrNull instanceof Timestamp ? tsOrNull.toDate() : new Date(tsOrNull);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fromDateInputValue(v) {
  if (!v) return null;
  const [y, m, d] = v.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return Timestamp.fromDate(new Date(y, m - 1, d, 0, 0, 0, 0));
}

function clampMonth(m) {
  const n = Number(m);
  if (!Number.isFinite(n)) return 1;
  return ((n - 1) % 12 + 12) % 12 + 1; // 1..12
}

function deriveQuarterMonths(startMonth) {
  const s = clampMonth(startMonth);
  return [0, 3, 6, 9].map((off) => ((s - 1 + off) % 12) + 1);
}

function nowYear() {
  return new Date().getFullYear();
}

function ensureTaskScheduling(s) {
  const y = nowYear();
  const year = Number(s?.year) || y;
  const months = (s?.months && typeof s.months === "object") ? s.months : {};
  return {
    v: 1,
    year,
    months, // object map: { "01": true, "02": true }
    lastScheduledAt: s?.lastScheduledAt ?? null,
    lastScheduledMonth: s?.lastScheduledMonth ?? null,
    // suggestion memory:
    lastEngineerId: s?.lastEngineerId ?? null,
    lastEngineerName: s?.lastEngineerName ?? null,
  };
}


const DOW = [
  { v: 1, label: "Mon" },
  { v: 2, label: "Tue" },
  { v: 3, label: "Wed" },
  { v: 4, label: "Thu" },
  { v: 5, label: "Fri" },
  { v: 6, label: "Sat" },
  { v: 0, label: "Sun" },
];

const MONTHS = [
  { v: 1, label: "Jan" },
  { v: 2, label: "Feb" },
  { v: 3, label: "Mar" },
  { v: 4, label: "Apr" },
  { v: 5, label: "May" },
  { v: 6, label: "Jun" },
  { v: 7, label: "Jul" },
  { v: 8, label: "Aug" },
  { v: 9, label: "Sep" },
  { v: 10, label: "Oct" },
  { v: 11, label: "Nov" },
  { v: 12, label: "Dec" },
];

function monthLabel(v) {
  return MONTHS.find((m) => m.v === v)?.label || String(v);
}

// ---------- cadence defaults ----------
function defaultCadence(type = "monthly") {
  const base = { v: 1, type };

  if (type === "daily") {
    return { ...base, daysOfWeek: [1, 2, 3, 4, 5] }; // Mon–Fri default
  }
  if (type === "weekly") {
    return { ...base, interval: 1 }; // 1=weekly, 2=fortnight
  }
  if (type === "monthly") {
    return { ...base, interval: 1 }; // 1=monthly, 2=bi-monthly
  }
  if (type === "quarterly") {
    const start = 1;
    return {
      ...base,
      interval: 1, // 1=every quarter, 2=every other quarter (6 months)
      quarterStartMonth: start,
      quarterMonths: deriveQuarterMonths(start),
    };
  }
  return { ...base, month: 1, day: 1 }; // yearly
}

function emptyTask() {
  return {
    id: uid(),
    name: "",
    description: "",
    checklist: [""],
    active: true,
    cadence: defaultCadence("monthly"),
    scheduling: ensureTaskScheduling(null),
  };
}


// ---------- UI bits ----------
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function PurpleButton({ children, className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    >
      {children}
    </button>
  );
}

function DangerButton({ children, className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-red-50 disabled:bg-gray-50 text-red-700 font-semibold",
        "px-4 py-2 rounded-lg border border-red-200 transition",
        className
      )}
    >
      {children}
    </button>
  );
}

function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}

function Select({ className, children, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    >
      {children}
    </select>
  );
}

function Pill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1 rounded-full text-sm font-semibold border transition",
        active
          ? "bg-purple-600 text-white border-purple-600"
          : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
      )}
    >
      {label}
    </button>
  );
}

// ---------- cadence editor ----------
function CadenceEditor({ value, onChange }) {
  const c = value || defaultCadence("monthly");

  function setType(type) {
    onChange(defaultCadence(type));
  }
  function patch(p) {
    onChange({ ...c, ...p });
  }
  function toggleDow(d) {
    const sel = new Set((c.daysOfWeek || []).map(Number));
    if (sel.has(d)) sel.delete(d);
    else sel.add(d);
    patch({ daysOfWeek: Array.from(sel).sort((a, b) => a - b) });
  }

  return (
    <div className="mt-3 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-800">Frequency</div>
          <Select value={c.type} onChange={(e) => setType(e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </Select>
        </div>

        {(c.type === "weekly" || c.type === "monthly") && (
          <div>
            <div className="text-sm font-semibold text-gray-800">Interval</div>
            <Select
              value={c.interval ?? 1}
              onChange={(e) => patch({ interval: Number(e.target.value) })}
            >
              <option value={1}>{c.type === "weekly" ? "Every week" : "Every month"}</option>
              <option value={2}>{c.type === "weekly" ? "Every 2 weeks" : "Every 2 months"}</option>
            </Select>
            <div className="text-xs text-gray-500 mt-1">
              Weekly/Monthly are once per period. Exact dates are chosen in the scheduler.
            </div>
          </div>
        )}
      </div>

      {c.type === "daily" && (
        <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
          <div className="text-sm font-semibold text-gray-800">On these days</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {DOW.map((d) => (
              <Pill
                key={d.v}
                label={d.label}
                active={new Set((c.daysOfWeek || []).map(Number)).has(d.v)}
                onClick={() => toggleDow(d.v)}
              />
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Any combination is fine (including weekends). The scheduler later decides if/when to create jobs.
          </div>
        </div>
      )}

      {c.type === "quarterly" && (
        <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-semibold text-gray-800">Interval</div>
              <Select
                value={c.interval ?? 1}
                onChange={(e) => patch({ interval: Number(e.target.value) })}
              >
                <option value={1}>Every quarter</option>
                <option value={2}>Every 6 months</option>
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                6 months = every other quarter.
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-800">Quarter start month</div>
              <Select
                value={c.quarterStartMonth ?? 1}
                onChange={(e) => {
                  const start = clampMonth(e.target.value);
                  patch({
                    quarterStartMonth: start,
                    quarterMonths: deriveQuarterMonths(start),
                  });
                }}
              >
                {MONTHS.map((m) => (
                  <option key={m.v} value={m.v}>
                    {m.label}
                  </option>
                ))}
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                Pick the first month of the quarter. The other months are automatic.
              </div>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">
              {c.interval === 2 ? "Run months (every 6 months)" : "Quarter months"}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(() => {
                const qMonths = deriveQuarterMonths(c.quarterStartMonth ?? 1);
                const monthsToShow = (Number(c.interval) === 2) ? [qMonths[0], qMonths[2]] : qMonths;
                return monthsToShow.map((m) => (
                  <span
                    key={m}
                    className="px-3 py-1 rounded-full text-sm font-semibold border bg-white text-gray-800 border-gray-200"
                  >
                    {monthLabel(m)}
                  </span>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {c.type === "yearly" && (
        <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-semibold text-gray-800">Month</div>
              <Select value={c.month ?? 1} onChange={(e) => patch({ month: clampMonth(e.target.value) })}>
                {MONTHS.map((m) => (
                  <option key={m.v} value={m.v}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-800">Day</div>
              <Input
                type="number"
                min={1}
                max={31}
                value={c.day ?? 1}
                onChange={(e) => patch({ day: Number(e.target.value) })}
              />
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            If the month has fewer days, your scheduler can clamp it later.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- main component ----------
export default function PPMAssetCreate() {
  const navigate = useNavigate();
  const { assetId } = useParams();
  const isEdit = Boolean(assetId);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [vendor, setVendor] = useState("");
  const [installedAt, setInstalledAt] = useState("");
  const [active, setActive] = useState(true);

  const [tasks, setTasks] = useState([emptyTask()]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // ensure selected task stays valid
  useEffect(() => {
    if (!tasks.length) return;
    if (!selectedTaskId) {
      setSelectedTaskId(tasks[0].id);
      return;
    }
    if (!tasks.some((t) => t.id === selectedTaskId)) {
      setSelectedTaskId(tasks[0].id);
    }
  }, [tasks, selectedTaskId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!assetId) return;
      setLoading(true);
      setError("");

      try {
        const ref = doc(db, "ppmassets", assetId);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Machine not found.");

        const data = snap.data();
        if (cancelled) return;

        setName(data.name ?? "");
        setCode(data.code ?? "");
        setLocation(data.location ?? "");
        setDepartment(data.department ?? "");
        setSerialNumber(data.serialNumber ?? "");
        setVendor(data.vendor ?? "");
        setInstalledAt(toDateInputValue(data.installedAt ?? null));
        setActive(data.active !== false);

        const incoming = Array.isArray(data.tasks) ? data.tasks : [];
        const loadedTasks =
          incoming.length
            ? incoming.map((t) => ({
                id: t.id || uid(),
                name: t.name ?? "",
                description: t.description ?? "",
                checklist: Array.isArray(t.checklist) ? (t.checklist.length ? t.checklist : [""]) : [""],
                active: t.active !== false,
                cadence: t.cadence?.v === 1 ? t.cadence : defaultCadence("monthly"),

                // ✅ NEW
                scheduling: ensureTaskScheduling(t.scheduling),
              }))
            : [emptyTask()];


        setTasks(loadedTasks);
        setSelectedTaskId(loadedTasks[0]?.id || null);
      } catch (e) {
        setError(e?.message || "Failed to load machine.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  const selectedTask = useMemo(
    () => tasks.find((t) => t.id === selectedTaskId) || tasks[0],
    [tasks, selectedTaskId]
  );

  const validation = useMemo(() => {
    const issues = [];
    if (!name.trim()) issues.push("Machine name is required.");
    if (!code.trim()) issues.push("Machine code/tag is required.");

    tasks.forEach((t, i) => {
      const label = `Task ${i + 1}`;
      if (!t.name.trim()) issues.push(`${label}: name is required.`);
      if (!t.cadence || t.cadence.v !== 1) issues.push(`${label}: cadence is missing or invalid.`);

      const ct = t.cadence?.type;
      const c = t.cadence || {};
      if (!["daily", "weekly", "monthly", "quarterly", "yearly"].includes(ct)) {
        issues.push(`${label}: cadence type is invalid.`);
        return;
      }

      if (ct === "daily") {
        if (!Array.isArray(c.daysOfWeek) || c.daysOfWeek.length === 0) {
          issues.push(`${label}: select at least one day for daily cadence.`);
        }
      }

      if (ct === "weekly" || ct === "monthly") {
        if (![1, 2].includes(Number(c.interval))) {
          issues.push(`${label}: interval must be every or every other.`);
        }
      }

      if (ct === "quarterly") {
        const start = Number(c.quarterStartMonth);
        if (!start || start < 1 || start > 12) {
          issues.push(`${label}: select a valid quarter start month.`);
        }
        if (![1, 2].includes(Number(c.interval ?? 1))) {
          issues.push(`${label}: quarterly interval must be 1 (quarterly) or 2 (6 months).`);
        }
      }

      if (ct === "yearly") {
        if (!c.month || c.month < 1 || c.month > 12) issues.push(`${label}: invalid month.`);
        if (!c.day || c.day < 1 || c.day > 31) issues.push(`${label}: invalid day.`);
      }
    });

    return { ok: issues.length === 0, issues };
  }, [name, code, tasks]);

  function updateTask(taskId, patch) {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
  }

  function updateChecklist(taskId, index, value) {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const list = Array.isArray(t.checklist) ? [...t.checklist] : [""];
        list[index] = value;
        return { ...t, checklist: list };
      })
    );
  }

  function addChecklistItem(taskId) {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const list = Array.isArray(t.checklist) ? [...t.checklist] : [];
        list.push("");
        return { ...t, checklist: list.length ? list : [""] };
      })
    );
  }

  function removeChecklistItem(taskId, index) {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const list = Array.isArray(t.checklist) ? [...t.checklist] : [""];
        list.splice(index, 1);
        return { ...t, checklist: list.length ? list : [""] };
      })
    );
  }

  function addTask() {
    const t = emptyTask();
    setTasks((prev) => [...prev, t]);
    setSelectedTaskId(t.id);
  }

  function removeTask(taskId) {
    setTasks((prev) => {
      const idx = prev.findIndex((t) => t.id === taskId);
      const next = prev.filter((t) => t.id !== taskId);
      const ensured = next.length ? next : [emptyTask()];

      // pick next selection
      const nextPick = ensured[Math.min(idx, ensured.length - 1)];
      setSelectedTaskId(nextPick.id);

      return ensured;
    });
  }

  function moveTask(taskId, dir) {
    setTasks((prev) => {
      const i = prev.findIndex((t) => t.id === taskId);
      if (i === -1) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;

      const next = [...prev];
      const tmp = next[i];
      next[i] = next[j];
      next[j] = tmp;
      return next;
    });
  }

  async function handleSave() {
    setError("");
    if (!validation.ok) {
      setError(validation.issues[0] || "Fix validation issues.");
      return;
    }

    setSaving(true);
    try {
      const cleanedTasks = tasks.map((t) => {
        const checklist = (Array.isArray(t.checklist) ? t.checklist : [])
          .map((x) => (x || "").trim())
          .filter(Boolean);

        const c = t.cadence || defaultCadence("monthly");
        const type = c.type || "monthly";

        const cadence = { v: 1, type };

        if (type === "daily") {
          cadence.daysOfWeek = Array.from(new Set((c.daysOfWeek || []).map(Number)))
            .filter((d) => [0, 1, 2, 3, 4, 5, 6].includes(d))
            .sort((a, b) => a - b);
        } else if (type === "weekly" || type === "monthly") {
          cadence.interval = [1, 2].includes(Number(c.interval)) ? Number(c.interval) : 1;
        } else if (type === "quarterly") {
          const start = clampMonth(c.quarterStartMonth ?? 1);
          cadence.interval = [1, 2].includes(Number(c.interval)) ? Number(c.interval) : 1;
          cadence.quarterStartMonth = start;
          cadence.quarterMonths = deriveQuarterMonths(start);
        } else if (type === "yearly") {
          cadence.month = clampMonth(c.month ?? 1);
          cadence.day = Math.min(31, Math.max(1, Number(c.day) || 1));
        }

       const sched = ensureTaskScheduling(t.scheduling);

        return {
          id: t.id || uid(),
          name: t.name.trim(),
          description: (t.description || "").trim(),
          checklist,
          active: t.active !== false,
          cadence,

          // ✅ NEW: persist scheduling state + lastEngineer fields
          scheduling: sched,
        };

      });

      const payload = {
        name: name.trim(),
        code: code.trim(),
        location: location.trim() || null,
        department: department.trim() || null,
        serialNumber: serialNumber.trim() || null,
        vendor: vendor.trim() || null,
        installedAt: fromDateInputValue(installedAt),
        active: Boolean(active),
        tasks: cleanedTasks,
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, "ppmassets", assetId), payload);
      } else {
        const newRef = doc(collection(db, "ppmassets"));
        await setDoc(newRef, { ...payload, createdAt: serverTimestamp() });
        navigate(`/PPMAssetCreate/${newRef.id}`);
        return;
      }
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-xl font-bold text-gray-900">PPM Machine</div>
        <div className="text-gray-600 mt-2">Loading…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">
            {isEdit ? "Edit Machine" : "Create Machine"}
          </h1>
          <p className="text-gray-600 mt-1">
            This saves machines + task templates into <span className="font-semibold">ppmassets</span>. No scheduling here.
          </p>
        </div>

        <div className="flex gap-2">
          <PurpleButton onClick={handleSave} disabled={saving || !validation.ok}>
            {saving ? "Saving…" : "Save"}
          </PurpleButton>
          <GhostButton onClick={() => navigate(-1)}>Back</GhostButton>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{error}</div>
          {!validation.ok && validation.issues.length > 1 ? (
            <ul className="list-disc ml-5 mt-2 text-sm">
              {validation.issues.slice(0, 6).map((x) => (
                <li key={x}>{x}</li>
              ))}
              {validation.issues.length > 6 ? <li>…and more</li> : null}
            </ul>
          ) : null}
        </div>
      ) : null}

      {/* Machine details */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900">Machine Details</h2>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 accent-purple-600"
            />
            Active
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-semibold text-gray-800">
              Machine Name <span className="text-red-500">*</span>
            </div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Press Brake 2" />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">
              Code / Tag <span className="text-red-500">*</span>
            </div>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. PB-002" />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">Location</div>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Bay 3" />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">Department</div>
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Production" />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">Serial Number</div>
            <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">Vendor</div>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800">Installed At</div>
            <Input type="date" value={installedAt} onChange={(e) => setInstalledAt(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900">Task Templates</h2>
            <p className="text-gray-600 text-sm mt-1">
              Select a task on the left to edit it on the right.
            </p>
          </div>
          <PurpleButton onClick={addTask}>+ Add Task</PurpleButton>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Task list */}
          <div className="lg:col-span-1 rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <div className="text-sm font-semibold text-gray-900">Tasks ({tasks.length})</div>
              <div className="text-xs text-gray-500">Click to edit</div>
            </div>

            <div className="max-h-[520px] overflow-auto">
              {tasks.map((t, idx) => {
                const isSel = t.id === (selectedTask?.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTaskId(t.id)}
                    className={cn(
                      "w-full text-left px-3 py-3 border-b border-gray-100 transition",
                      isSel ? "bg-purple-50" : "hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold text-gray-500">Task {idx + 1}</div>
                        <div className={cn("font-extrabold", t.name?.trim() ? "text-gray-900" : "text-gray-400")}>
                          {t.name?.trim() ? t.name : "Untitled task"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {t.active !== false ? "Active" : "Inactive"} · {t.cadence?.type || "monthly"}
                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <GhostButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            moveTask(t.id, -1);
                          }}
                          disabled={idx === 0}
                          className="px-2 py-1 text-xs"
                        >
                          ↑
                        </GhostButton>
                        <GhostButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            moveTask(t.id, +1);
                          }}
                          disabled={idx === tasks.length - 1}
                          className="px-2 py-1 text-xs"
                        >
                          ↓
                        </GhostButton>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected task editor */}
          <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white">
            {selectedTask ? (
              <>
                <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-500">Editing</div>
                    <div className="text-xl font-extrabold text-gray-900">
                      {selectedTask.name?.trim() ? selectedTask.name : "Untitled task"}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <input
                        type="checkbox"
                        checked={selectedTask.active !== false}
                        onChange={(e) => updateTask(selectedTask.id, { active: e.target.checked })}
                        className="h-4 w-4 accent-purple-600"
                      />
                      Active
                    </label>
                    <DangerButton onClick={() => removeTask(selectedTask.id)}>Remove</DangerButton>
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">
                        Task Name <span className="text-red-500">*</span>
                      </div>
                      <Input
                        value={selectedTask.name}
                        onChange={(e) => updateTask(selectedTask.id, { name: e.target.value })}
                        placeholder="e.g. Electrical checks"
                      />
                    </div>

                    <div>
                      <div className="text-sm font-semibold text-gray-800">Description</div>
                      <Input
                        value={selectedTask.description}
                        onChange={(e) => updateTask(selectedTask.id, { description: e.target.value })}
                        placeholder="Short summary (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-extrabold text-gray-900">
                      Cadence <span className="text-red-500">*</span>
                    </div>
                    <div className="text-gray-600 text-sm mt-1">
                      Daily = choose weekdays. Weekly/Monthly = interval only. Quarterly = start month (derived). Yearly = fixed date.
                    </div>

                    <CadenceEditor
                      value={selectedTask.cadence}
                      onChange={(cadence) => updateTask(selectedTask.id, { cadence })}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-extrabold text-gray-900">Checklist</div>
                        <div className="text-sm text-gray-600">Optional steps for the engineer later.</div>
                      </div>
                      <GhostButton onClick={() => addChecklistItem(selectedTask.id)}>+ Add step</GhostButton>
                    </div>

                    <div className="mt-3 space-y-2">
                      {(selectedTask.checklist || [""]).map((item, i) => (
                        <div key={`${selectedTask.id}_${i}`} className="flex gap-2">
                          <Input
                            value={item}
                            onChange={(e) => updateChecklist(selectedTask.id, i, e.target.value)}
                            placeholder={`Step ${i + 1}`}
                          />
                          <GhostButton onClick={() => removeChecklistItem(selectedTask.id, i)}>Remove</GhostButton>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-6 text-gray-600">No task selected.</div>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <PurpleButton onClick={handleSave} disabled={saving || !validation.ok}>
            {saving ? "Saving…" : "Save Machine"}
          </PurpleButton>
          <GhostButton onClick={() => navigate(-1)}>Cancel</GhostButton>
        </div>
      </div>
    </div>
  );
}
